import { cn } from '@/src/renderer/lib/utils'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/src/renderer/components/ui/tooltip'
import type { ButtonGroupProps } from './ButtonGroup.types'
import './ButtonGroup.scss'

/**
 * Segmented button group (shadcn-style joined outline buttons). The segments
 * are momentary **buttons** — there is no persistent selection; each click
 * fires `onSelect` and the segment springs back to Rest. In Figma the component
 * is still called "Window Mode Toggle Group"; the code uses shadcn's
 * `ButtonGroup`, because a shadcn `ToggleGroup` keeps a selection.
 *
 * Each segment moves through drei Zustände:
 * - **Rest** — neutral (white bg, input border, soft shadow, grey icon).
 * - **Hover** — the cursor is directly over the segment: violet, raised
 *   (translateY −1px + lifted shadow).
 * - **Pressed** — while the button is held down: violet, pushed down
 *   (translateY +1px, shadow drops). Releasing returns to Rest.
 *
 * **Kein „Near"** (entfernt 2026-09-12): Bis dahin färbten sich beide Zeichen
 * violett, sobald der Zeiger in die Nähe kam — eine Farbänderung, die niemand
 * angefordert hatte und die im Blick herumzappelte. Mit ihr fällt auch der
 * `mousemove`-Listener je Instanz weg.
 *
 * Dark mode is handled automatically by the mode-aware `--colors-violet-*` tokens.
 *
 * If an item has a `shortcut`, it is surfaced only as a tooltip on hover/focus
 * (not inline on the segment).
 *
 * @example
 * <ButtonGroup
 *   aria-label="Window mode"
 *   onSelect={(mode) => applyWindowMode(mode)}
 *   items={[
 *     { value: 'list', label: 'Liste', icon: <WindowListIcon /> },
 *     { value: 'stack', label: 'Stapel', icon: <GalleryVerticalEnd /> },
 *   ]}
 * />
 */
export function ButtonGroup({
  items,
  onSelect,
  disabled = false,
  iconOnly = false,
  className,
  ...rest
}: ButtonGroupProps) {
  const renderButton = (item: ButtonGroupProps['items'][number]) => (
    <button
      key={item.value}
      type="button"
      className="button-group__item"
      disabled={disabled || item.disabled}
      onClick={() => {
        if (!disabled && !item.disabled) onSelect(item.value)
      }}
    >
      {item.icon && <span className="button-group__icon">{item.icon}</span>}
      <span className="button-group__label">{item.label}</span>
    </button>
  )

  return (
    <div
      className={cn('button-group', { 'button-group--icon-only': iconOnly }, className)}
      role="group"
      aria-label={rest['aria-label']}
    >
      <TooltipProvider>
        {items.map((item) =>
          // The shortcut is surfaced only on hover/focus as a tooltip — never
          // shown inline on the segment. The tooltip names the segment too (an
          // icon-only group shows no label), a shortcut never stands alone.
          item.shortcut ? (
            <Tooltip key={item.value}>
              <TooltipTrigger asChild>{renderButton(item)}</TooltipTrigger>
              <TooltipContent shortcut={item.shortcut}>{item.label}</TooltipContent>
            </Tooltip>
          ) : (
            renderButton(item)
          ),
        )}
      </TooltipProvider>
    </div>
  )
}
